import { useCallback, useEffect, useState } from "react";

import { useResizeObserver } from "utilities/resizeObserver/useResizeObserver";

interface IUseOverflowDetectionOptions {
    /**
     * Whether to enable overflow detection
     */
    enabled: boolean;

    /**
     * Number of items being displayed - used to trigger re-check when items change
     */
    itemCount: number;
}

/**
 * Custom hook to detect if content overflows its container horizontally or vertically
 * Uses ResizeObserver for efficient and accurate overflow detection
 *
 * @example
 * const { containerRef, hasOverflow } = useOverflowDetection({
 *     enabled: isReadOnly && !showAllTags && tags.length > 0,
 *     itemCount: tags.length
 * });
 *
 * <div ref={containerRef} style={{ flexWrap: showAll ? "wrap" : "nowrap" }}>
 *     {tags}
 * </div>
 * {hasOverflow && <Button onClick={() => setShowAll(true)}>Show All</Button>}
 */
export function useOverflowDetection({ enabled, itemCount }: IUseOverflowDetectionOptions) {
    const [hasOverflow, setHasOverflow] = useState(false);

    const checkOverflow = useCallback((entry: ResizeObserverEntry | undefined) => {
        if (!entry) return;

        const container = entry.target as HTMLElement;
        // Check for both horizontal overflow (nowrap mode) and vertical overflow (wrap mode)
        const isOverflowing =
            container.scrollWidth > container.clientWidth ||
            container.scrollHeight > container.clientHeight;
        setHasOverflow(isOverflowing);
    }, []);

    const [containerRef, setContainerRef] = useResizeObserver<HTMLDivElement>(checkOverflow);

    // Reset overflow state when disabled
    useEffect(() => {
        if (!enabled) {
            setHasOverflow(false);
        }
    }, [enabled]);

    // Recheck overflow when item count changes
    useEffect(() => {
        if (enabled && containerRef.current) {
            const container = containerRef.current;
            // Check for both horizontal overflow (nowrap mode) and vertical overflow (wrap mode)
            const isOverflowing =
                container.scrollWidth > container.clientWidth ||
                container.scrollHeight > container.clientHeight;
            setHasOverflow(isOverflowing);
        }
    }, [enabled, itemCount, containerRef]);

    return { containerRef: setContainerRef, hasOverflow };
}
